import React, { useContext, useEffect, useState, ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import { AuthenticationApi, LoginResponse } from 'api-client';
import { getApiConfiguration } from '../../api/utils';
import { Button } from '../../components/ui/button';

const getClient = () => {
  return new AuthenticationApi(getApiConfiguration());
};

const MagicLinkLogin = () => {
  const { setUserDetails } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'error' | 'retry' | 'success'>('loading');
  const [error, setError] = useState<ReactNode>('');
  const [retryCount, setRetryCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(4);

  const handleMagicLinkLogin = async (token: string) => {
    if (retryCount >= 3) {
      setStatus('error');
      setError(
        <>
          Too many failed attempts. Please{' '}
          <Link to="/magic-link" className="text-blue-500 hover:text-blue-400 underline">
            request a new magic link
          </Link>
          .
        </>
      );
      return;
    }

    setIsLoading(true);
    setError('');

    const client = getClient();

    try {
      const response: LoginResponse = await client.apiAuthMagicLinkLoginCreate({
        magicLinkLogin: { token }
      });

      if (response.status === "success" && response.jwt) {
        setUserDetails({
          user: response.jwt.user,
          access: response.jwt.access,
          refresh: response.jwt.refresh
        });
        setStatus('success');
        
        // Start countdown
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              navigate('/');
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        return () => clearInterval(timer);
      } else {
        throw new Error(response.detail || 'Unknown error occurred');
      }
    } catch (error: any) {
      setRetryCount(prev => prev + 1);
      setStatus('retry');
      
      if (error.response) {
        try {
          const errorData = await error.response.json();
          if (errorData.detail) {
            setError(errorData.detail);
          } else if (errorData.non_field_errors?.includes("E-mail is not verified.")) {
            setError(
              <>
                Please verify your email address before logging in.{' '}
                <Link to="/resend-verification" className="text-blue-500 hover:text-blue-400 underline">
                  Resend verification email
                </Link>
              </>
            );
          } else if (errorData.non_field_errors?.[0]) {
            setError(errorData.non_field_errors[0]);
          } else if (typeof errorData === 'string') {
            setError(errorData);
          } else {
            setError('Failed to login. Please try again.');
          }
        } catch (jsonError) {
          setError('An unexpected error occurred. Please try again.');
        }
      } else if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Network error. Please check your connection and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log('MagicLinkLogin component mounted');
    const token = searchParams.get('token');
    if (!token) {
      console.log('No token found in URL');
      setStatus('error');
      setError('Invalid magic link. Please request a new one.');
      return;
    }

    handleMagicLinkLogin(token);
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-8">
          {/* Header */}
          <div className="flex items-center justify-center mb-8">
            <Shield className="h-10 w-10 text-blue-500" />
            <h1 className="text-2xl font-bold text-white ml-3">Magic Link Login</h1>
          </div>

          {/* Status */}
          <div className="text-center">
            {status === 'loading' ? (
              <div className="text-blue-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p>Logging you in...</p>
              </div>
            ) : status === 'success' ? (
              <div className="space-y-4">
                <div className="text-green-500">
                  <Shield className="h-12 w-12 mx-auto mb-4" />
                  <p className="text-lg font-semibold">Successfully logged in!</p>
                </div>
                <p className="text-gray-400">
                  Redirecting you to the dashboard in {countdown} seconds...
                </p>
              </div>
            ) : status === 'retry' ? (
              <div className="space-y-4">
                <p className="text-red-500">{error}</p>
                <Button
                  className="w-full"
                  onClick={() => {
                    const token = searchParams.get('token');
                    if (token) handleMagicLinkLogin(token);
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? 'Retrying...' : 'Try Again'}
                </Button>
                <p className="text-sm text-gray-400">
                  Or{' '}
                  <Link to="/magic-link" className="text-blue-500 hover:text-blue-400">
                    request a new magic link
                  </Link>
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-red-500">{error}</p>
                <Link to="/magic-link">
                  <Button className="w-full">
                    Request New Magic Link
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MagicLinkLogin;
